'use client';

import React, { useEffect, useState } from 'react';
import { ProductInformationDocumentData } from 'prismicio-types';
import fetchAndTransformGoogleSizeGuideData from '@app/lib/google/methods/fetch-google-size-guide-data';
import { PrismicRichText } from '@prismicio/react';
import { StaticImage } from 'mmds';
import { TableData } from '@app/lib/google/size-guide';
import SizeGuideTable from './size-guide-table';
import { processProductMetafields } from '../utilities';
import cn from 'clsx';
import s from './product-assistance.module.css';

interface SizeGuideProps {
    product: any; // TODO
    prismicSizeGuide: ProductInformationDocumentData | null;
    shopifyProductSizing: string | null;
}

const SizeGuide = ({
    product,
    prismicSizeGuide,
    shopifyProductSizing,
}: SizeGuideProps) => {
    const [googleSizeGuideData, setGoogleSizeGuideData] =
        useState<TableData | null>(null);
    const [failed, setFailed] = useState(false);
    const metafields = processProductMetafields(product);
    const { gender } = metafields;
    const productType = product?.productType;
    const { description, image, measurementInstructions, context } =
        prismicSizeGuide || {};

    useEffect(() => {
        let isMounted = true;

        fetchAndTransformGoogleSizeGuideData().then((data) => {
            if (isMounted) {
                if (!data) setFailed(true);
                else setGoogleSizeGuideData(data);
            }
        });

        return () => {
            isMounted = false;
        };
    }, [product]);

    const richTextComponents = {
        paragraph: ({ children }) => (
            <p className={cn('mmds-copy-one')}>{children}</p>
        ),
        heading3: ({ children }) => (
            <h3 className="mmds-copy-one-fluid">{children}</h3>
        ),
    };

    const contextRichText = {
        paragraph: ({ children }) => (
            <p className="mmds-copy-three">{children}</p>
        ),
    };

    if (failed)
        return (
            <div className={cn(s.stack, 'mmds-copy-one')}>
                We couldn&lsquo;t fetch the size guide this time. Please try
                again or <a href="https://support.maap.cc/">contact us</a> for
                assistance.
            </div>
        );

    if (!googleSizeGuideData) {
        return (
            <div className={cn(s.stack, 'mmds-copy-one')}>
                Loading size guide...
            </div>
        );
    }

    // Removes "</br>" from the shopify product sizing value
    const modifiedProductSizing = shopifyProductSizing?.replace(
        /<\/?br\s?\/?>/g,
        ''
    );

    return (
        <div className={cn(s.stack, 'mmds-copy-one')}>
            {shopifyProductSizing && (
                <p style={{ whiteSpace: 'pre-wrap' }}>
                    {modifiedProductSizing}
                </p>
            )}
            <PrismicRichText
                field={description}
                components={richTextComponents}
            />
            <p className={s.productType}>{productType}</p>
            <div className={s.measurementInstructions}>
                {image && image?.url && (
                    <>
                        <hr className="divider" data-color="steel" />
                        <StaticImage
                            desktopImage={image.url}
                            mobileImage={image.url}
                            desktopWidth="240"
                            mobileWidth="240"
                        />
                    </>
                )}
                {measurementInstructions &&
                    measurementInstructions.length > 0 && (
                        <div className="mmds-copy-three">
                            <ol className={s.stack} data-flow-space="small">
                                {measurementInstructions?.map(
                                    ({ bodyPart, instruction }, index) => (
                                        <li key={`${bodyPart}-${index}`}>
                                            <PrismicRichText field={bodyPart} />
                                            <PrismicRichText
                                                field={instruction}
                                            />
                                        </li>
                                    )
                                )}
                            </ol>
                        </div>
                    )}
                <hr className="divider" data-color="steel" />
            </div>
            {context && (
                <PrismicRichText field={context} components={contextRichText} />
            )}
            <SizeGuideTable
                googleSizeGuideData={googleSizeGuideData}
                gender={gender}
                productType={productType}
            />
        </div>
    );
};

export default SizeGuide;
